import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Shield, Target, Eye, Network, Search, Code, 
  Globe, AlertTriangle, CheckCircle, ArrowRight,
  Clock, Send, Award, FileText, CheckCircle2,
  MapPin, FileSearch, Newspaper, Building2,
  Brain, Satellite, PlayCircle, Database
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { SEOHead } from '@/components/SEOHead'

export default function Training() {
  const [selectedProgram, setSelectedProgram] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    programs: '',
    country: '',
    message: ''
  })
  const [submitted, setSubmitted] = useState(false)

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      const { apiClient } = await import('@/lib/api')
      const response = await apiClient.submitTrainingInterest({
        name: formData.name,
        email: formData.email,
        company: formData.organization || undefined,
        course_name: selectedProgram || formData.programs || 'General Inquiry',
        message: formData.message || undefined
      })
      
      if (response.success) {
        setSubmitted(true)
        setTimeout(() => {
          setSubmitted(false)
          setFormData({ name: '', organization: '', email: '', programs: '', country: '', message: '' })
          setSelectedProgram(null)
        }, 5000)
      }
    } catch (error: any) {
      console.error('Failed to submit information request:', error)
      alert(error.message || 'Failed to submit request. Please try again.')
    }
  }

  const programs = [
    {
      id: 'digital-osint',
      title: 'Digital OSINT & Threat Mapping',
      duration: '24 Hours',
      level: 'Intermediate',
      icon: FileSearch,
      description: 'Learn advanced OSINT collection, metadata tracing, and cross-platform threat correlation techniques used by cyber journalists and investigators.',
      color: 'text-green-400'
    },
    {
      id: 'geopolitical-intel',
      title: 'Geopolitical Intelligence & Conflict Analysis',
      duration: '30 Hours',
      level: 'Advanced',
      icon: Globe,
      description: 'Study real-world case studies of political conflict, data-driven situational mapping, and sentiment-based forecasting across regions.',
      color: 'text-red-400'
    },
    {
      id: 'executive-protection',
      title: 'Executive Protection & Digital Footprint Management',
      duration: '20 Hours',
      level: 'Expert',
      icon: Shield,
      description: 'Learn to protect high-profile individuals and executives by monitoring social leaks, data exposure, and travel risk intelligence.',
      color: 'text-blue-400'
    },
    {
      id: 'investigative-journalism',
      title: 'Investigative Journalism for Security Analysts',
      duration: '25 Hours',
      level: 'Intermediate',
      icon: Newspaper,
      description: 'Master journalistic OSINT methods for exposing corruption, misinformation networks, and disinformation campaigns safely and ethically.',
      color: 'text-purple-400'
    },
    {
      id: 'venture-threat',
      title: 'Venture & Corporate Threat Management',
      duration: '28 Hours',
      level: 'Expert',
      icon: Building2,
      description: 'Designed for VCs, CISOs, and Risk Officers — assess startup security, fraud detection, and geopolitical risk exposure before investments.',
      color: 'text-yellow-400'
    },
    {
      id: 'psychological-profiling',
      title: 'Psychological Profiling & Influence Campaigns',
      duration: '18 Hours',
      level: 'Advanced',
      icon: Brain,
      description: 'Learn psychological patterning used in influence ops, fake narrative campaigns, and social engineering threats.',
      color: 'text-pink-400'
    },
    {
      id: 'geoint-satellite',
      title: 'GEOINT & Satellite Reconnaissance',
      duration: '30 Hours',
      level: 'Specialist',
      icon: Satellite,
      description: 'Use open satellite data, thermal mapping, and geolocation for real-world conflict or disaster verification.',
      color: 'text-cyan-400'
    }
  ]

  return (
    <>
      <SEOHead
        title="Training Programs - Sycek OSINT | Elite Intelligence Training Institute"
        description="TRAIN TO PROTECT, INVESTIGATE & INTELLIGENCE-INFORM. Professional OSINT and intelligence training for journalists, intelligence officers, and executive protection teams."
        keywords="OSINT training, intelligence training, threat intelligence, security training, investigative journalism, executive protection training"
        canonical="/training"
      />
      
      <div className="min-h-screen bg-[#0a0f0a] text-white relative overflow-hidden">
        {/* Animated Hexagonal Grid Background with Parallax */}
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `
              linear-gradient(rgba(34, 197, 94, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(34, 197, 94, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
            animation: 'gridMove 20s linear infinite',
          }}
        />
        
        {/* Animated Scan Lines */}
        <div className="absolute inset-0 pointer-events-none opacity-10">
          {[...Array(3)].map((_, i) => (
            <div 
              key={i}
              className="absolute inset-0 bg-gradient-to-b from-transparent via-green-500/30 to-transparent h-0.5 animate-scan"
              style={{ 
                top: `${30 + i * 25}%`,
                animationDelay: `${i * 1}s`,
                animationDuration: '4s'
              }} 
            />
          ))}
        </div>
        
        {/* Matrix-style Data Streams */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-32 bg-gradient-to-b from-green-500/40 via-green-400/60 to-transparent animate-dataStream"
              style={{
                left: `${15 + i * 12}%`,
                top: '-128px',
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${5 + i * 0.3}s`
              }}
            />
          ))}
        </div>
        
        {/* Floating OSINT Icons with Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[Search, Target, Shield, Network, Eye, Code, MapPin, Database, Globe, FileSearch].map((Icon, i) => (
            <div
              key={i}
              className="absolute animate-float"
              style={{
                left: `${10 + (i * 12)}%`,
                top: `${15 + (i * 8)}%`,
                animationDelay: `${i * 0.5}s`,
                animationDuration: `${6 + (i % 3)}s`
              }}
            >
              <Icon
                className="text-green-500/10 blur-sm"
                style={{
                  width: `${40 + (i % 3) * 20}px`,
                  height: `${40 + (i % 3) * 20}px`,
                }}
              />
            </div>
          ))}
        </div>

        {/* Animated Data Streams */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-20 bg-gradient-to-b from-green-500/30 to-transparent animate-dataStream"
              style={{
                left: `${20 + i * 20}%`,
                top: '-80px',
                animationDelay: `${i * 0.8}s`,
                animationDuration: `${4 + i * 0.5}s`
              }}
            />
          ))}
        </div>

        {/* Glowing Orbs with Movement */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-green-500/5 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDuration: '8s' }} />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-500/5 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDelay: '1s', animationDuration: '10s' }} />
          <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-green-500/3 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDelay: '2s', animationDuration: '12s' }} />
          <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-red-500/3 rounded-full blur-3xl animate-pulse animate-float" style={{ animationDelay: '3s', animationDuration: '9s' }} />
        </div>
        
        {/* Radial Gradient Overlays */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-gradient-radial from-green-500/5 via-transparent to-transparent" />
          <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-red-500/5 via-transparent to-transparent" />
        </div>

        {/* Header */}
        <header className="relative z-10 border-b border-green-500/20 bg-[#0a0f0a]/80 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-600 rounded-lg flex items-center justify-center shadow-lg shadow-green-500/50">
                  <span className="text-black font-bold text-lg font-mono">S</span>
                </div>
                <div className="absolute -inset-1 bg-green-500/20 rounded-lg blur-sm animate-pulse" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-green-400 font-mono drop-shadow-[0_0_10px_rgba(34,197,94,0.6)]">
                  Sycek
                </h1>
                <p className="text-xs text-green-500/70 font-mono">// intelligence training institute</p>
              </div>
            </Link>
            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-300 hover:text-green-400 transition-colors">Home</Link>
              <Link to="/training" className="text-green-400 font-semibold">Training</Link>
              <a href="https://community.sycek.io" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-green-400 transition-colors">Community</a>
              <Link to="/register">
                <Button className="bg-green-500 hover:bg-green-600 text-black font-semibold shadow-lg shadow-green-500/50">
                  Access Platform
                </Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative z-10 py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-green-500/20 text-green-400 border border-green-500/50 font-mono text-xs">
                // CLASSIFIED TRAINING PROTOCOLS
              </Badge>
              
              <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight relative">
                <span className="text-white relative inline-block">
                  THE FRONTLINE
                  <span className="absolute inset-0 text-green-400 blur-sm opacity-50 animate-pulse">THE FRONTLINE</span>
                </span>
                <br />
                <span className="text-green-400 drop-shadow-[0_0_30px_rgba(34,197,94,0.8)] relative inline-block animate-glow">
                  OF DIGITAL WARFARE
                  <span className="absolute inset-0 text-green-300 blur-md opacity-70 animate-pulse">OF DIGITAL WARFARE</span>
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto mb-8 leading-relaxed">
                Training the next generation of cyber operatives.
              </p>
            </div>

            {/* Warning Banner with Pulse and Glow */}
            <div className="max-w-4xl mx-auto mb-16 relative">
              <div className="bg-red-900/30 border-2 border-red-500/50 rounded-lg p-4 flex items-center gap-3 backdrop-blur-sm relative overflow-hidden group">
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-red-500/10 to-red-500/0 animate-pulse" />
                <div className="absolute inset-0 bg-red-500/5 group-hover:bg-red-500/10 transition-colors duration-300" />
                
                {/* Scanning Line Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-red-400/30 to-transparent animate-scan" style={{ width: '30%', animationDuration: '3s' }} />
                
                <div className="relative z-10 flex items-center gap-3">
                  <div className="relative">
                    <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 animate-pulse" />
                    <div className="absolute inset-0 w-6 h-6 text-red-400/50 blur-sm animate-pulse" style={{ animationDelay: '0.5s' }} />
                  </div>
                  <p className="text-white font-semibold group-hover:text-red-300 transition-colors">
                    WARNING: ADVANCED SURVEILLANCE TECHNIQUES - AUTHORIZED PERSONNEL ONLY
                  </p>
                </div>
                
                {/* Corner Indicators */}
                <div className="absolute top-2 left-2 w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                <div className="absolute top-2 right-2 w-2 h-2 bg-red-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
                <div className="absolute bottom-2 left-2 w-2 h-2 bg-red-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute bottom-2 right-2 w-2 h-2 bg-red-400 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }} />
              </div>
            </div>

            {/* Stats Cards with Enhanced Animations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20">
              <Card className="bg-black/50 border border-red-500/30 backdrop-blur-sm hover:border-red-500/60 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500/0 to-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-2xl group-hover:bg-red-500/20 transition-all" />
                <CardContent className="p-8 text-center relative z-10">
                  <div className="text-6xl font-bold text-red-500 mb-4 drop-shadow-[0_0_15px_rgba(239,68,68,0.6)] group-hover:scale-110 transition-transform duration-300">
                    08
                  </div>
                  <div className="text-gray-300 text-lg font-semibold group-hover:text-white transition-colors">Specialized Domains</div>
                  <div className="mt-4 flex justify-center gap-1">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="w-1 h-6 bg-red-500/30 rounded-full group-hover:bg-red-500/60 transition-all" style={{ animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-2xl group-hover:bg-green-500/20 transition-all" />
                <CardContent className="p-8 text-center relative z-10">
                  <div className="text-6xl font-bold text-green-400 mb-4 drop-shadow-[0_0_15px_rgba(34,197,94,0.6)] group-hover:scale-110 transition-transform duration-300">
                    200+
                  </div>
                  <div className="text-gray-300 text-lg font-semibold group-hover:text-white transition-colors">Hours of Practical Labs</div>
                  <div className="mt-4 w-full bg-green-500/10 rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-400 h-full rounded-full animate-progress" style={{ width: '85%' }} />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-black/50 border border-gray-500/30 backdrop-blur-sm hover:border-gray-400/60 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-500/0 to-gray-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-500/10 rounded-full blur-2xl group-hover:bg-gray-500/20 transition-all" />
                <CardContent className="p-8 text-center relative z-10">
                  <div className="text-6xl font-bold text-white mb-4 group-hover:scale-110 transition-transform duration-300 relative">
                    20+
                    <Globe className="absolute -top-2 -right-2 w-8 h-8 text-green-400 animate-spin-slow" />
                  </div>
                  <div className="text-gray-300 text-lg font-semibold group-hover:text-white transition-colors">Global Alumni from Countries</div>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {['🇺🇸', '🇬🇧', '🇨🇦', '🇦🇺', '🇩🇪', '🇫🇷'].map((flag, i) => (
                      <span key={i} className="text-2xl animate-bounce" style={{ animationDelay: `${i * 0.1}s` }}>{flag}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Training Programs */}
        <section className="relative z-10 py-20 bg-black/20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 relative">
              <div className="absolute left-1/2 top-0 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
              <h2 className="text-4xl md:text-5xl font-bold mb-4 relative">
                <span className="text-white relative inline-block">
                  Training
                  <span className="absolute inset-0 text-green-400 blur-sm opacity-50">Training</span>
                </span>
                <span className="text-green-400 ml-3 relative inline-block animate-glow">
                  Programs
                  <span className="absolute inset-0 text-green-300 blur-md opacity-70 animate-pulse">Programs</span>
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto relative z-10">
                Professional intelligence training designed for real-world application
              </p>
              <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {programs.map((program, index) => (
                <Card 
                  key={program.id} 
                  className="bg-black/50 border border-green-500/20 hover:border-green-500/50 backdrop-blur-sm transition-all duration-500 group relative overflow-hidden"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Animated Background Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-green-500/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
                  
                  {/* Corner Accent */}
                  <div className="absolute top-0 right-0 w-20 h-20 border-t border-r border-green-500/20 group-hover:border-green-500/50 transition-colors" />
                  <div className="absolute top-2 right-2 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  
                  <CardHeader className="relative z-10">
                    <div className={`w-14 h-14 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 ${program.color}`}>
                      <program.icon className="w-7 h-7 group-hover:scale-110 transition-transform" />
                    </div>
                    <CardTitle className="text-white group-hover:text-green-400 transition-colors mb-2 text-lg leading-tight">
                      {program.title}
                    </CardTitle>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <Badge variant="outline" className="border-green-500/30 text-green-400 text-xs group-hover:bg-green-500/10 transition-colors">
                        <Clock className="w-3 h-3 mr-1" />
                        {program.duration}
                      </Badge>
                      <Badge variant="outline" className="border-gray-500/30 text-gray-400 text-xs group-hover:bg-gray-500/10 transition-colors">
                        {program.level}
                      </Badge>
                    </div>
                    <CardDescription className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">
                      {program.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="relative z-10">
                    <Button
                      className="w-full bg-green-500 hover:bg-green-600 text-black font-semibold group-hover:shadow-lg group-hover:shadow-green-500/50 transition-all duration-300 hover:scale-105"
                      onClick={() => {
                        setSelectedProgram(program.title)
                        setTimeout(() => {
                          document.getElementById('request-info-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }, 100)
                      }}
                    >
                      <span className="flex items-center justify-center">
                        Request Info
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                  </CardContent>
                  
                  {/* Hover Effect Lines */}
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-500 group-hover:w-full transition-all duration-500" />
                  <div className="absolute top-0 left-0 w-0 h-0.5 bg-green-500 group-hover:w-full transition-all duration-500 delay-100" />
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section className="relative z-10 py-20 bg-black/40">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 relative">
              <div className="absolute left-1/2 top-0 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
              <h2 className="text-4xl md:text-5xl font-bold mb-4 relative">
                <span className="text-white relative inline-block">
                  Training
                  <span className="absolute inset-0 text-green-400 blur-sm opacity-50">Training</span>
                </span>
                <span className="text-green-400 ml-3 relative inline-block animate-glow">
                  Methodology
                  <span className="absolute inset-0 text-green-300 blur-md opacity-70 animate-pulse">Methodology</span>
                </span>
              </h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto relative z-10">
                Our comprehensive approach combines theory, hands-on practice, and real-world scenarios
              </p>
              <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto relative">
              {/* Animated Connecting Lines */}
              <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-green-500/30 to-transparent transform -translate-y-1/2 z-0">
                <div className="absolute left-0 top-0 w-3 h-3 bg-green-400 rounded-full shadow-lg shadow-green-400/50 animate-pulse" />
                <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-r from-green-500/50 via-green-400/80 to-green-500/50 animate-scan" style={{ width: '30%', animation: 'scanLine 3s linear infinite' }} />
              </div>
              
              {/* Connecting Dots */}
              {[0, 1, 2, 3].map((i) => (
                <div 
                  key={i}
                  className="hidden lg:block absolute top-1/2 w-4 h-4 bg-green-500/30 border-2 border-green-400 rounded-full transform -translate-y-1/2 z-10 animate-pulse"
                  style={{ 
                    left: `${12.5 + i * 25}%`,
                    animationDelay: `${i * 0.3}s`
                  }}
                />
              ))}

              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-500 relative z-10 group overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-green-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                <CardHeader className="relative z-10">
                  <div className="w-14 h-14 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <PlayCircle className="w-7 h-7 text-green-400 group-hover:text-green-300" />
                  </div>
                  <CardTitle className="text-white group-hover:text-green-400 transition-colors">Live Intelligence Labs</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors">
                    Hands-on investigations with real datasets and interactive guidance from expert instructors.
                  </p>
                  <div className="mt-4 flex gap-1">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="w-full h-1 bg-green-500/20 rounded-full overflow-hidden">
                        <div className="h-full bg-green-400 rounded-full animate-progress" style={{ width: `${60 + i * 15}%`, animationDelay: `${i * 0.2}s` }} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-500 relative z-10 group overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-green-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                <CardHeader className="relative z-10">
                  <div className="w-14 h-14 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Target className="w-7 h-7 text-green-400 group-hover:text-green-300" />
                  </div>
                  <CardTitle className="text-white group-hover:text-green-400 transition-colors">Scenario Simulations</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors">
                    Field-style threat response and team-based exercises in controlled, realistic environments.
                  </p>
                  <div className="mt-4 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Target className="w-12 h-12 text-green-500/10 group-hover:text-green-500/20 transition-all group-hover:scale-150" />
                    </div>
                    <div className="relative">
                      <div className="w-16 h-16 border-2 border-green-500/30 rounded-full mx-auto group-hover:border-green-500/60 transition-colors animate-ping" style={{ animationDuration: '2s' }} />
                      <div className="absolute inset-0 w-10 h-10 border-2 border-green-400/50 rounded-full mx-auto top-3 left-3" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-500 relative z-10 group overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-green-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                <CardHeader className="relative z-10">
                  <div className="w-14 h-14 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <FileText className="w-7 h-7 text-green-400 group-hover:text-green-300" />
                  </div>
                  <CardTitle className="text-white group-hover:text-green-400 transition-colors">Case-Based Analysis</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors">
                    Learn from actual global incidents, leaks, and intelligence operations with detailed breakdowns.
                  </p>
                  <div className="mt-4 space-y-2">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                        <div className="flex-1 h-1 bg-green-500/20 rounded-full overflow-hidden">
                          <div className="h-full bg-green-400 rounded-full animate-progress" style={{ width: `${40 + i * 15}%`, animationDelay: `${i * 0.3}s` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-500 relative z-10 group overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-green-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                <CardHeader className="relative z-10">
                  <div className="w-14 h-14 rounded-lg bg-green-500/10 flex items-center justify-center mb-4 group-hover:bg-green-500/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                    <Award className="w-7 h-7 text-green-400 group-hover:text-green-300" />
                  </div>
                  <CardTitle className="text-white group-hover:text-green-400 transition-colors">Professional Certification</CardTitle>
                </CardHeader>
                <CardContent className="relative z-10">
                  <p className="text-gray-300 text-sm group-hover:text-gray-200 transition-colors">
                    Digital badge + lifetime access to course materials and future updates.
                  </p>
                  <div className="mt-4 flex justify-center">
                    <div className="relative">
                      <Award className="w-16 h-16 text-green-400/50 group-hover:text-green-400 transition-colors animate-pulse" />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <CheckCircle2 className="w-8 h-8 text-green-400/70 group-hover:text-green-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Certification Section */}
        <section className="relative z-10 py-20 bg-black/20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-500 group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-green-500/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
                
                <CardHeader className="relative z-10">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="relative group/badge">
                      <div className="w-24 h-24 rounded-lg bg-gradient-to-br from-green-500/20 to-emerald-600/20 flex items-center justify-center border-2 border-green-500/50 group-hover:border-green-400 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                        <Award className="w-12 h-12 text-green-400 group-hover:text-green-300 transition-colors" />
                      </div>
                      <div className="absolute -inset-2 bg-green-500/20 rounded-lg blur-md animate-pulse group-hover:bg-green-500/30 transition-colors" />
                      <div className="absolute -inset-4 bg-green-500/10 rounded-lg blur-xl animate-pulse" style={{ animationDelay: '0.5s' }} />
                      {/* Rotating Ring */}
                      <div className="absolute -inset-3 border-2 border-green-500/30 rounded-lg animate-spin-slow" style={{ animationDuration: '8s' }} />
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-white mb-1">Sycek Certified Intelligence Professional</CardTitle>
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/50 font-mono text-xs mt-2">
                        SCIP
                      </Badge>
                      <CardDescription className="text-gray-400 text-base mt-2">
                        Industry-recognized certification for intelligence professionals
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 relative z-10">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { title: 'Industry Recognition', desc: 'Certificates recognized by security professionals and organizations worldwide', icon: '🏆' },
                      { title: 'Digital Badge', desc: 'Shareable credentials for LinkedIn and professional profiles', icon: '🎖️' },
                      { title: 'CE Credits', desc: 'Continuing education credits for maintaining professional certifications', icon: '📚' },
                      { title: 'Lifetime Access', desc: 'Access to course materials and updates after completion', icon: '♾️' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3 group/item hover:bg-green-500/5 rounded-lg p-3 transition-all duration-300">
                        <div className="relative">
                          <CheckCircle2 className="w-6 h-6 text-green-400 mt-1 flex-shrink-0 group-hover/item:scale-110 transition-transform" />
                          <div className="absolute inset-0 w-6 h-6 text-green-400/50 blur-sm animate-pulse" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl">{item.icon}</span>
                            <h4 className="font-semibold text-white group-hover/item:text-green-400 transition-colors">{item.title}</h4>
                          </div>
                          <p className="text-sm text-gray-400 group-hover/item:text-gray-300 transition-colors">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Request Information Form */}
        <section id="request-info-form" className="relative z-10 py-20 bg-black/40">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <Card className="bg-black/50 border border-green-500/30 backdrop-blur-sm hover:border-green-500/60 transition-all duration-500 group relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute -top-20 -right-20 w-64 h-64 bg-green-500/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-500" />
                
                {/* Animated Border Glow */}
                <div className="absolute inset-0 border-2 border-green-500/0 group-hover:border-green-500/30 rounded-lg transition-all duration-500" />
                
                <CardHeader className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <CardTitle className="text-3xl text-green-400 mb-2 group-hover:text-green-300 transition-colors">
                      Request Information or Corporate Enrollment
                    </CardTitle>
                  </div>
                  <CardDescription className="text-gray-400 text-base group-hover:text-gray-300 transition-colors">
                    Complete the form below and our admissions team will contact you within 24 hours
                  </CardDescription>
                </CardHeader>
                <CardContent className="relative z-10">
                  {submitted ? (
                    <div className="text-center py-8">
                      <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-green-400 mb-2">Request Submitted</h3>
                      <p className="text-gray-300">
                        Our admissions team will contact you within 24 hours with enrollment options and upcoming training batch details.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleFormSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Full Name *
                        </label>
                        <Input
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="bg-black/50 border-green-500/30 text-white focus:border-green-500"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Organization / Role *
                        </label>
                        <Input
                          required
                          value={formData.organization}
                          onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                          className="bg-black/50 border-green-500/30 text-white focus:border-green-500"
                          placeholder="Security Analyst, Intelligence Officer, etc."
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Email Address *
                        </label>
                        <Input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="bg-black/50 border-green-500/30 text-white focus:border-green-500"
                          placeholder="john@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Interested Program(s) *
                        </label>
                        <Input
                          required
                          value={formData.programs}
                          onChange={(e) => setFormData({ ...formData, programs: e.target.value })}
                          className="bg-black/50 border-green-500/30 text-white focus:border-green-500"
                          placeholder={selectedProgram || "e.g., Digital OSINT & Threat Mapping"}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Country / Region *
                        </label>
                        <Input
                          required
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                          className="bg-black/50 border-green-500/30 text-white focus:border-green-500"
                          placeholder="United States"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-2">
                          Message or Inquiry
                        </label>
                        <textarea
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full bg-black/50 border border-green-500/30 rounded-lg p-3 text-white resize-none focus:border-green-500 focus:outline-none"
                          placeholder="Tell us about your training needs, group enrollment, or specific questions..."
                          rows={4}
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-green-500 hover:bg-green-600 text-black font-semibold text-lg py-6 shadow-lg shadow-green-500/50 hover:shadow-green-500/70 transition-all relative overflow-hidden group/btn"
                      >
                        <span className="relative z-10 flex items-center justify-center">
                          <Send className="w-5 h-5 mr-2 group-hover/btn:translate-x-1 transition-transform" />
                          Submit Request
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
                        <div className="absolute inset-0 bg-green-400/20 blur-xl opacity-0 group-hover/btn:opacity-100 transition-opacity animate-pulse" />
                      </Button>

                      <p className="text-xs text-gray-500 text-center mt-4 italic">
                        Our admissions team will contact you within 24 hours with enrollment options and upcoming training batch details.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="relative z-10 py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16 relative">
              <div className="absolute left-1/2 top-0 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
              <h2 className="text-4xl md:text-5xl font-bold mb-4 relative">
                <span className="text-white relative inline-block">
                  Frequently Asked
                  <span className="absolute inset-0 text-green-400 blur-sm opacity-50">Frequently Asked</span>
                </span>
                <span className="text-green-400 ml-3 relative inline-block animate-glow">
                  Questions
                  <span className="absolute inset-0 text-green-300 blur-md opacity-70 animate-pulse">Questions</span>
                </span>
              </h2>
              <div className="absolute left-1/2 bottom-0 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-green-500/50 to-transparent" />
            </div>

            <div className="max-w-4xl mx-auto space-y-4">
              {[
                {
                  q: 'Are these courses recognized by industry bodies?',
                  a: 'Yes. Our certifications are recognized by security professionals and organizations worldwide. The Sycek Certified Intelligence Professional (SCIP) credential is designed to meet industry standards for intelligence analysis and OSINT operations.'
                },
                {
                  q: 'Can corporate teams enroll privately?',
                  a: 'Absolutely. We offer customized corporate training programs tailored to your organization\'s specific needs. Contact us through the enrollment form to discuss private group training, on-site delivery options, and customized curricula.'
                },
                {
                  q: 'What is the mode of delivery (online/live labs)?',
                  a: 'Our training combines live online sessions with hands-on virtual lab environments. You\'ll participate in real-time instructor-led sessions, conduct practical exercises in secure virtual environments, and analyze real-world case studies. All sessions are recorded for later review.'
                },
                {
                  q: 'Are the programs suitable for government or law enforcement?',
                  a: 'Yes. Our training programs are designed for professionals across sectors including government, law enforcement, military, private security, and corporate intelligence. All courses emphasize legal and ethical frameworks for intelligence gathering and analysis.'
                },
                {
                  q: 'Can I request a customized training bundle?',
                  a: 'Yes. We offer flexible training packages that combine multiple courses to meet your specific learning objectives. Contact our admissions team to discuss custom training bundles, multi-course discounts, and enterprise licensing options.'
                }
              ].map((faq, idx) => (
                <Card key={idx} className="bg-black/50 border border-green-500/20 backdrop-blur-sm hover:border-green-500/40 transition-all">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-start">
                      <AlertTriangle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                      <span>{faq.q}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pl-12">
                    <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}